import { memo, useEffect, useRef } from 'react';

const SCRIPT_ID = 'google-translate-script';
const CONTAINER_ID = 'google_translate_element';
const SUPPORTED_LANGUAGES = ['de', 'en', 'pl', 'fi', 'nl', 'fr', 'el'] as const;

const GoogleTranslateWidgetComponent = () => {
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const initializeWidget = () => {
      if (hasInitializedRef.current || window.__googleTranslateInitialized) {
        return;
      }

      const translateElement = window.google?.translate?.TranslateElement;

      if (!translateElement) {
        return;
      }

      const inlineLayout =
        translateElement.InlineLayout &&
        (translateElement.InlineLayout as Record<string, unknown>).HORIZONTAL;

      new translateElement(
        {
          pageLanguage: 'en',
          includedLanguages: SUPPORTED_LANGUAGES.join(','),
          layout: inlineLayout,
          autoDisplay: false,
        },
        CONTAINER_ID,
      );

      hasInitializedRef.current = true;
      window.__googleTranslateInitialized = true;
    };

    window.googleTranslateElementInit = initializeWidget;

    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      if (window.google?.translate?.TranslateElement) {
        initializeWidget();
      }

      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.defer = true;
    script.onerror = () => {
      console.error('Google Translate script failed to load.');
    };

    document.body.appendChild(script);
  }, []);

  return (
    <div className='flex flex-col items-center gap-3 text-white'>
      <span className='text-sm font-semibold uppercase tracking-wider'>
        Translate this page
      </span>
      <div
        id={CONTAINER_ID}
        aria-live='polite'
        aria-label='Translate this page using Google Translate'
        className='min-h-[36px] max-w-full overflow-hidden'
      />
    </div>
  );
};

GoogleTranslateWidgetComponent.displayName = 'GoogleTranslateWidget';

export default memo(GoogleTranslateWidgetComponent);

